import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGetBroker } from "../api";
import { useAuth } from "../components/AuthContext";

function StatCard({ title, value, subtitle, color = "#DC2626", onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 20,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)")}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 32, fontWeight: 900, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

export default function CRMDashboard() {
  const nav = useNavigate();
  const { adminEmail } = useAuth();
  const [stats, setStats] = useState(null);
  const [chatStats, setChatStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function loadData() {
    setLoading(true);
    setErr("");
    try {
      const [crmRes, chatRes] = await Promise.all([
        apiGetBroker("/crm/stats", { adminEmail }),
        fetch("/api/chatbot/stats").then(r => r.json()).catch(() => ({ stats: {} }))
      ]);
      setStats(crmRes.stats);
      setChatStats(chatRes.stats);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [adminEmail]);

  const pipelineValue = stats?.opportunities?.pipelineValue || 0;

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: 20 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900 }}>CRM Dashboard</h1>
        <p style={{ margin: "8px 0 0 0", color: "#6b7280" }}>Customer relationship management</p>
      </div>

      {err && (
        <div style={{ marginBottom: 16, padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626" }}>
          {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <button
          onClick={() => nav("/app/broker/crm/contacts")}
          style={{ padding: "10px 20px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
        >
          + New Contact
        </button>
        <button
          onClick={() => nav("/app/broker/crm/chats")}
          style={{ padding: "10px 20px", background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
        >
          💬 Chat Sessions
        </button>
        <button onClick={loadData} style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
      ) : (
        <>
          {/* CRM Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <StatCard
              title="Total Contacts"
              value={stats?.contacts?.total || 0}
              subtitle={`${stats?.contacts?.active || 0} active`}
              onClick={() => nav("/app/broker/crm/contacts")}
            />
            <StatCard
              title="Pending Activities"
              value={stats?.activities?.pending || 0}
              subtitle={`${stats?.activities?.total || 0} total`}
              color="#f59e0b"
            />
            <StatCard
              title="Open Opportunities"
              value={stats?.opportunities?.open || 0}
              subtitle={`${stats?.opportunities?.total || 0} total`}
              color="#3b82f6"
            />
            <StatCard
              title="Pipeline Value"
              value={`$${(pipelineValue / 100).toLocaleString()}`}
              subtitle="Potential revenue"
              color="#10b981"
            />
          </div>

          {/* Chatbot Stats */}
          {chatStats && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: 18 }}>💬 Chatbot Performance</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
                <StatCard
                  title="Chat Sessions"
                  value={chatStats.totalSessions || 0}
                  color="#8b5cf6"
                  onClick={() => nav("/app/broker/crm/chats")}
                />
                <StatCard
                  title="Leads Captured"
                  value={chatStats.leadsCapture || chatStats.crmLeads || 0}
                  subtitle="From chatbot"
                  color="#10b981"
                  onClick={() => nav("/app/broker/crm/chats")}
                />
                <StatCard
                  title="Conversion Rate"
                  value={`${chatStats.conversionRate || 0}%`}
                  subtitle="Visitor → Lead"
                  color="#f59e0b"
                />
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 16, padding: 20, background: "#f9fafb", borderRadius: 12, border: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 12px 0" }}>🚀 CRM Features</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20, color: "#374151" }}>
            <li>Contact management with company associations</li>
            <li>Activity tracking (calls, emails, meetings)</li>
            <li>Sales opportunity pipeline</li>
          </ul>
          <ul style={{ margin: 0, paddingLeft: 20, color: "#374151" }}>
            <li>AI Chatbot with lead capture</li>
            <li>Partner role assignments (SAP-compatible)</li>
            <li>Conversation history & analytics</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
